import { useCallback, useEffect, useMemo, useState } from 'react';
import { getFinanceLockStatus } from '../services/financeLockApi';
import useAuthStore from '../store/useAuthStore';
import { hasFinanceRole, isOwnerAdminRole } from '../utils/rolePermissions';

function getErrorMessage(error) {
  return error?.response?.data?.error || error?.message || 'Finance Lock status could not be checked.';
}

export default function useFinanceLockStatus({ autoLoad = true } = {}) {
  const user = useAuthStore((state) => state.user);
  const [statusPayload, setStatusPayload] = useState(null);
  const [loading, setLoading] = useState(Boolean(autoLoad));
  const [error, setError] = useState('');
  const [restricted, setRestricted] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setRestricted(false);

    try {
      const data = await getFinanceLockStatus();
      setStatusPayload(data);
      return data;
    } catch (err) {
      const status = err?.response?.status;
      setStatusPayload(null);

      if (status === 403) {
        setRestricted(true);
        setError('You do not have permission to unlock finance data.');
      } else {
        setError(getErrorMessage(err));
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      refresh();
    }
  }, [autoLoad, refresh]);

  useEffect(() => {
    const handleFinanceTokenChanged = () => {
      refresh();
    };

    window.addEventListener('finance-unlock-token-changed', handleFinanceTokenChanged);
    return () => {
      window.removeEventListener('finance-unlock-token-changed', handleFinanceTokenChanged);
    };
  }, [refresh]);

  const financeLock = statusPayload?.financeLock || {};
  const enabled = Boolean(financeLock.enabled);
  const unlocked = !enabled || Boolean(financeLock.unlocked);
  const locked = enabled && !financeLock.unlocked;

  return useMemo(
    () => ({
      canManage: isOwnerAdminRole(user?.role),
      canUnlock: Array.isArray(user?.capabilities) ? ['finance.view', 'finance.profitability.view', 'billing.invoice.create', 'billing.payment.collect', 'billing.emi.manage', 'inventory.view'].some(capability => user.capabilities.includes(capability)) : hasFinanceRole(user?.role),
      enabled,
      error,
      financeLock,
      loading,
      locked,
      refresh,
      restricted,
      statusPayload,
      unlocked,
      user,
    }),
    [
      enabled,
      error,
      financeLock,
      loading,
      locked,
      refresh,
      restricted,
      statusPayload,
      unlocked,
      user,
    ]
  );
}
